import { useEffect, useRef } from 'react';

const dateFmt = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'short',
  timeStyle: 'short'
});

const formatDate = value => dateFmt.format(new Date(value));

const formatDuration = ms => {
  const totalSeconds = Math.max(0, Math.round(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`;
};

const getRunStatusDisplay = run => {
  if (run.status !== 'completed') return run.status.charAt(0).toUpperCase() + run.status.slice(1);
  const verb = run.isDryRun ? 'Previewed' : 'Completed';
  if (!run.startedAt || !run.finishedAt) return verb;
  return `${verb} in ${formatDuration(new Date(run.finishedAt) - new Date(run.startedAt))}`;
};

// A script reports progress by logging a line that contains "Progress: ..."; the newest one is shown above the log.
const getLatestProgress = logs => {
  const matches = String(logs || '').match(/Progress: [^\n"]+/g);
  return matches ? matches[matches.length - 1].slice('Progress: '.length).trim() : null;
};

const ScriptRunnerResults = ({
  selectedRun,
  selectedRunUser,
  selectedRunLogs,
  isLoadingSelectedRunLogs,
  onRefreshLogs,
  onPromoteDryRun,
  isSubmitting,
  canRunSelectedScript
}) => {
  const logRef = useRef(null);
  const stickToBottom = useRef(true);
  useEffect(() => {
    stickToBottom.current = true;
  }, [selectedRun?.id]);

  useEffect(() => {
    const node = logRef.current;
    if (!node || !stickToBottom.current || selectedRun?.status !== 'running') return;
    node.scrollTop = node.scrollHeight;
  }, [selectedRunLogs, selectedRun?.status]);

  if (!selectedRun) return null;

  const logLineCount = selectedRunLogs ? selectedRunLogs.split('\n').length : 0;
  const latestProgress = getLatestProgress(selectedRunLogs);

  const onLogScroll = () => {
    const node = logRef.current;
    if (!node) return;
    stickToBottom.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
  };

  return (
    <section className='script-runner-section script-runner-run-section'>
      <div className='script-runner-section-content'>
        <div className='script-runner-run-heading'>
          <h2>Run #{selectedRun.id}</h2>
          <div className='script-runner-run-meta'>
            <span>{getRunStatusDisplay(selectedRun)}</span>
            <span>{formatDate(selectedRun.createdAt || selectedRun.startedAt)}</span>
            {selectedRunUser && <span>{selectedRunUser.name || selectedRunUser.email}</span>}
          </div>
        </div>

        {latestProgress && (
          <div className='script-runner-progress'>
            <span>Progress</span>
            <p>{latestProgress}</p>
          </div>
        )}

        {selectedRun.statusMsg && <div className='script-runner-status-message'>{selectedRun.statusMsg}</div>}

        {Boolean(selectedRun.isDryRun) && (
          <div className='script-runner-dryrun-banner'>
            <p>This was a dry run — nothing was saved</p>
            <button
              type='button'
              className='script-runner-primary-btn script-runner-small-btn'
              onClick={onPromoteDryRun}
              disabled={isSubmitting || !canRunSelectedScript}
            >
              {isSubmitting ? 'Applying...' : 'Apply These Changes'}
            </button>
          </div>
        )}

        <div className='script-runner-raw-log'>
          <div className='script-runner-raw-log-header'>
            <span className='script-runner-raw-log-label'>Raw Log</span>
            <div className='script-runner-raw-log-actions'>
              {logLineCount > 0 && (
                <span>
                  {logLineCount.toLocaleString()} {logLineCount === 1 ? 'line' : 'lines'}
                </span>
              )}
              <button
                type='button'
                className='script-runner-refresh-btn'
                onClick={() => onRefreshLogs(selectedRun.id)}
                disabled={isLoadingSelectedRunLogs || selectedRun.status === 'pending'}
              >
                {isLoadingSelectedRunLogs ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>
          {!selectedRunLogs && isLoadingSelectedRunLogs ? (
            <div className='script-runner-raw-log-loading'>Loading logs...</div>
          ) : !selectedRunLogs && selectedRun.status === 'pending' ? (
            <div className='script-runner-raw-log-loading'>Script is starting...</div>
          ) : (
            <pre ref={logRef} onScroll={onLogScroll}>
              {selectedRunLogs}
            </pre>
          )}
        </div>
      </div>
    </section>
  );
};

export default ScriptRunnerResults;
